import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Smartphone, ArrowRight, Check, MapPin, Camera, 
  Clock, Navigation, CheckCircle, FileText, Sparkles, MessageSquare
} from 'lucide-react';

const MobilePage = () => {
  const features = [
    {
      icon: MapPin,
      title: "Today's Route",
      description: "Crews see their complete route for the day with all job details, property info, and special instructions."
    },
    {
      icon: Navigation,
      title: "One-Tap Navigation",
      description: "Tap any job to get turn-by-turn directions. Integration with your phone's maps makes getting there easy."
    },
    {
      icon: Clock,
      title: "Start & Complete Jobs",
      description: "Mark arrival, start the job, and complete it with simple taps. Time tracking happens automatically."
    },
    {
      icon: Camera,
      title: "Photo Documentation",
      description: "Take before and after photos directly in the app. Photos are automatically attached to the job record."
    },
    {
      icon: FileText,
      title: "Property Details",
      description: "Access service instructions, gate codes, and special notes for every property—even offline."
    },
    {
      icon: CheckCircle,
      title: "Real-Time Updates",
      description: "Job completions sync instantly to the office. Everyone stays on the same page."
    }
  ];

  const workflow = [
    { step: "1", title: "Open App", description: "See today's assigned route" },
    { step: "2", title: "Navigate", description: "Tap for directions to first job" },
    { step: "3", title: "Arrive", description: "Check property instructions & gate code" },
    { step: "4", title: "Start Job", description: "Tap to begin work timer" },
    { step: "5", title: "Take Photos", description: "Capture before/after images" },
    { step: "6", title: "Complete", description: "Mark done and move to next" }
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Smartphone className="h-4 w-4" />
                For Field Crews
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Mobile Crew App
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                A simple, fast mobile experience designed for crews in the field. 
                View routes, complete jobs, and upload photos—all with minimal taps.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-primary hover:bg-primary/90">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                <div className="bg-white rounded-[2.5rem] w-72 overflow-hidden">
                  <div className="bg-primary text-white p-4">
                    <p className="text-sm opacity-80">Today's Route</p>
                    <p className="text-xl font-semibold">Monday, June 15</p>
                    <p className="text-sm opacity-80 mt-1">8 jobs • Team Alpha</p>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                      <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">123 Oak Street</p>
                        <p className="text-xs text-gray-500">Completed</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border-2 border-amber-200">
                      <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                        <Clock className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">456 Maple Ave</p>
                        <p className="text-xs text-amber-600">In Progress</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-600">3</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">789 Pine Road</p>
                        <p className="text-xs text-gray-500">10:30 AM</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-600">4</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">321 Elm Drive</p>
                        <p className="text-xs text-gray-500">11:45 AM</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple Crew Workflow</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Designed for fast, efficient use in the field. Minimal taps to get the job done.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {workflow.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Built for the Field</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything crews need to do their jobs efficiently, right in their pocket.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-gray-100 bg-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Designed for Real Work
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We know crews are wearing gloves, working in the sun, and don't have time for complicated apps. 
                That's why we built something that works in the real world.
              </p>
              <ul className="space-y-4">
                {[
                  "Large buttons for gloved fingers",
                  "High contrast for outdoor visibility",
                  "Offline support for spotty coverage",
                  "Fast loading, no waiting around",
                  "Minimal training needed"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-teal-50 rounded-2xl p-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Crew Feedback</h4>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 italic">"Finally an app that doesn't slow us down. We can see our route, check gate codes, and mark jobs done in seconds."</p>
                    <p className="text-sm font-medium text-gray-900 mt-2">— Crew Lead, 3 years experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Integration Section */}
      <section className="py-20 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI-Powered
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Manage Crews from Anywhere
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                The AI Assistant connects the office to the field. Ask about job status, 
                reassign crews on the fly, or check today's progress—all with simple questions.
              </p>
              <ul className="space-y-4">
                {[
                  "Check crew progress in real-time",
                  "Get instant status updates by asking",
                  "Reassign jobs without touching the app",
                  "Know exactly where every crew is"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/features/ai-assistant">
                  <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                    Learn About AI Assistant <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-violet-100 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-white">AI Assistant</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-violet-50 rounded-lg p-4 ml-8">
                  <p className="text-sm text-gray-700">"How is Team Alpha doing today?"</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mr-8">
                  <p className="text-sm text-gray-700">Team Alpha's progress today:</p>
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Completed</span>
                      <span className="font-medium text-emerald-600">5 jobs</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>In Progress</span>
                      <span className="font-medium text-amber-600">1 job</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Remaining</span>
                      <span className="font-medium text-gray-600">2 jobs</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-emerald-500 h-2 rounded-full" style={{width: '62%'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Empower Your Crews
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Give your field teams the tools they need to work efficiently and stay connected.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default MobilePage;
